import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const StudentAuthContext = createContext({});

export const useStudentAuth = () => useContext(StudentAuthContext);

export const StudentAuthProvider = ({ children }) => {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [token, setToken] = useState(() => {
    return localStorage.getItem('student_token');
  });
  
  const navigate = useNavigate();
  
  // Create axios instance for student API
  const studentApi = axios.create({
    baseURL: 'https://multi-schoolsuite.cbthost.com',
  });
  
  // Add token to requests if it exists
  studentApi.interceptors.request.use(
    (config) => {
      const studentToken = localStorage.getItem('student_token');
      if (studentToken) {
        config.headers.Authorization = `Bearer ${studentToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Check token validity
  const isTokenValid = (token) => {
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (error) {
      return false;
    }
  };

  // Load student on mount
  useEffect(() => {
    const loadStudent = async () => {
      const storedToken = localStorage.getItem('student_token');
      
      if (!storedToken || !isTokenValid(storedToken)) {
        if (storedToken) {
          localStorage.removeItem('student_token');
          localStorage.removeItem('student_data');
        }
        setLoading(false);
        return;
      }

      try {
        // Verify token with backend
        const response = await studentApi.get('/student-auth/verify', {
          params: { token: storedToken }
        });
        
        if (response.data.valid) {
          const studentData = {
            student_reg: response.data.student_reg,
            name: response.data.name,
            class_name: response.data.class_name,
            isStudent: true
          };
          
          localStorage.setItem('student_data', JSON.stringify(studentData));
          setStudent(studentData);
          setToken(storedToken);
            // ✅ ADD THIS
          await fetchProfile();
        } else {
          logout();
        }
      } catch (error) {
        console.error('Student auth error:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    loadStudent();
  }, []);

  useEffect(() => {
  if (token && !profile) {
    fetchProfile();
  }
}, [token]);

  const studentLogin = async (studentReg, password, regCode) => {   // <-- Add regCode parameter
  try {
    const response = await axios.post('https://multi-schoolsuite.cbthost.com/student-auth/login', {
      student_reg: studentReg,
      password: password,
      reg_code: regCode   // <-- Send reg_code in payload
    });
    
    const { access_token, student_reg, name, class_name } = response.data;
    
    localStorage.setItem('student_token', access_token);
    localStorage.setItem('student_data', JSON.stringify({
      student_reg,
      name,
      class_name,
      isStudent: true
    }));
    
    setToken(access_token);
    setStudent({
      student_reg,
      name,
      class_name,
      isStudent: true
    });
    await fetchProfile();
    
    toast.success('Student login successful!');
    navigate('/student/dashboard');
    
    return { success: true };
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Student login failed');
    return { success: false };
  }
};

const fetchProfile = async () => {
  try {
    const res = await studentApi.get('/student-portal/profile');
    setProfile(res.data);
  } catch (error) {
    console.error('Failed to load profile:', error);
  }
};
  
  const logout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_data');
    setToken(null);
    setStudent(null);
    navigate('/student_login');
    toast.success('Logged out successfully');
  };

  const getStudentToken = () => {
    return localStorage.getItem('student_token');
  };

  const value = {
    student,
    token,
    loading,
    studentLogin,
     profile, 
    logout,
    getStudentToken,
    isAuthenticated: !!student && !!token && isTokenValid(token),
    studentApi  // Export the student-specific API instance
  };

  return (
    <StudentAuthContext.Provider value={value}>
      {!loading && children}
    </StudentAuthContext.Provider>
  );
};